import { useEffect } from 'react';

import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { FormControl } from '@mui/material';
import { useGetOrderAdjustmentTypes } from 'pages/OrderAdjustments/hooks/useGetOrderAdjustmentTypes';
import { OrderAdjustmentsFormValues } from 'pages/OrderAdjustments/hooks/useOrderAdjustmentsForm';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormHelperText } from 'shared/mui/FormHelperText/FormHelperText';
import { InputLabel } from 'shared/mui/Labels/InputLabel/InputLabel';
import { MenuItem } from 'shared/mui/MenuItem/MenuItem';
import { Select } from 'shared/mui/Select/Select';
import { Tooltip } from 'shared/mui/Tooltip';
import { Typography } from 'shared/mui/Typography';

export const OrderAdjustmentsAdjustmentTypeSelect = () => {
  const { control, setValue } = useFormContext<OrderAdjustmentsFormValues>();
  const translate = useTypedTranslate();
  const orderAdjustmentTypes = useGetOrderAdjustmentTypes();
  const adjustmentType = useWatch({ control, name: 'adjustmentType' });

  const isAdjustmentTypeAvailable = orderAdjustmentTypes.some(
    (type) => type.id === adjustmentType && type.isAvailable,
  );

  useEffect(() => {
    if (adjustmentType && !isAdjustmentTypeAvailable) {
      setValue('adjustmentType', null);
    }
  }, [adjustmentType, isAdjustmentTypeAvailable]);

  return (
    <Controller
      control={control}
      name="adjustmentType"
      render={({ field, fieldState }) => (
        <FormControl fullWidth variant="outlined">
          <InputLabel error={!!fieldState.error} id="adjustmentType" required shrink>
            {translate('app.orderAdjustments.adjustmentType')}
          </InputLabel>
          <Select
            error={!!fieldState.error}
            label={translate('app.orderAdjustments.adjustmentType')}
            labelId="adjustmentType"
            notched
            onChange={field.onChange}
            renderValue={(value) =>
              orderAdjustmentTypes.find((type) => type.id === value)?.description
            }
            required
            size="small"
            value={field?.value}
          >
            {orderAdjustmentTypes?.map((item) => (
              <MenuItem
                disabled={!item.isAvailable}
                key={item.id}
                sx={{ display: 'flex', justifyContent: 'space-between' }}
                value={item.id}
              >
                <Typography component="p" variant="body2">
                  {item.description}
                </Typography>
                <Tooltip
                  arrow
                  onClick={(e) => e.stopPropagation()}
                  placement="right-start"
                  sx={{
                    pointerEvents: 'auto',
                  }}
                  title={<Typography fontSize={12}>{item.tooltip}</Typography>}
                >
                  <InfoOutlined color="secondary" />
                </Tooltip>
              </MenuItem>
            ))}
          </Select>
          <FormHelperText error={!!fieldState.error} sx={{ height: '20px' }}>
            {fieldState.error?.message && translate('app.orderAdjustments.required')}
          </FormHelperText>
        </FormControl>
      )}
      rules={{ required: translate('app.orderAdjustments.required') }}
    />
  );
};
